/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {PRESET_DURATIONS} from '@ion/dls/src/maps/components/timeMode/current/constants';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DateSelectorValues, TimeModes, ToolbarEnum, TrackingMode} from '@ion/dls/src/types/types';
// the store is reached by path rather than by the `@/` alias, as the hooks
// beside this one do - the coverage run from the repo root cannot resolve it ...
import {
  setMapWidgetSelected,
  setPersistLiveHistory,
  setTimelineDuration,
  setTimelinePreset,
  setTimeMode,
  setTimeModePanelOpen
} from '../../../../store/actions/tracking';
import {
  historyActiveSelector,
  liveHistoryActiveSelector,
  liveHistorySearchTimeSelector,
  mapWidgetSelectedSelector,
  timeModeSelector,
  trackingFiltersSelector,
  trackingModeSelector
} from '../../../../store/selectors/tracking-selectors';
import {applyHistoryTimeMode} from './useApplyHistoryTimeMode';

/**
 * The useSyncLegacyTimeMode hook moves the current theme's Time Mode control
 * to the report a legacy theme run left behind - the legacy theme has no
 * Time Mode control, so switching themes mid-report leaves the control on
 * Live with no chip in the filter bar for the report still on screen:
 *
 * - an Asset History report moves the control to History over the range
 *   the report's own date filter holds, the same way a deep link's does.
 * - a Live History report moves it to Timeline over the interval the
 *   report searches by - lit on its own chip where the Timeline offers
 *   one, and on Custom otherwise. The report is left running as it is.
 * - the legacy Live History widget is closed, running or not - the current
 *   theme has no button for it, so nothing there could put it away again.
 *   Only the widget goes: its own Close would cancel the report as well.
 *
 * Only a control still reading Live is moved - the legacy theme never moves
 * it, while the current theme's own reports move it before they run - so
 * a mode picked over a running report in the current theme is never undone.
 * Re-checked whenever the theme, the mode, either report or the widget
 * changes.
 *
 * ```typescript
 *
 *   import useSyncLegacyTimeMode from './hooks/history/useSyncLegacyTimeMode';
 *   ...
 *
 *   useSyncLegacyTimeMode();
 *
 * ```
 */
export const useSyncLegacyTimeMode = () => {
  const dispatch              = useDispatch();
  const {isCurrent}           = useThemeContext();
  const timeMode              = useSelector(timeModeSelector);
  const trackingMode          = useSelector(trackingModeSelector);
  const isHistoryActive       = useSelector(historyActiveSelector);
  const isLiveHistoryActive   = useSelector(liveHistoryActiveSelector);
  const liveHistorySearchTime = useSelector(liveHistorySearchTimeSelector);
  const filters               = useSelector(trackingFiltersSelector);
  const mapWidgetSelected     = useSelector(mapWidgetSelectedSelector);

  // closed ahead of the Timeline sync below - left open, the widget cancels
  // whatever live history is running at the next filter change, Timeline's
  // own included. Its persisted flag goes too, or the legacy toolbar would
  // bring the widget back the next time its other tools close ...
  useEffect(() => {
    if (isCurrent && mapWidgetSelected === ToolbarEnum.LiveHistory) {
      dispatch(setMapWidgetSelected(null));
      dispatch(setPersistLiveHistory(false));
    }
  }, [isCurrent, mapWidgetSelected]);

  useEffect(() => {
    if (isCurrent && timeMode === TimeModes.live) {
      if (trackingMode === TrackingMode.History && !isHistoryActive) {
        const {startDate, endDate, period} = filters?.date || {};
        applyHistoryTimeMode(dispatch, {
          preset : period || DateSelectorValues.Today,
          from   : startDate,
          to     : endDate
        });
      } else if (trackingMode === TrackingMode.Live && isLiveHistoryActive) {
        const hasPreset = PRESET_DURATIONS.includes(liveHistorySearchTime);
        dispatch(setTimelinePreset(hasPreset ? liveHistorySearchTime : null));
        dispatch(setTimelineDuration(liveHistorySearchTime));
        dispatch(setTimeMode(TimeModes.timeline));
        dispatch(setTimeModePanelOpen(false));
      }
    }
  }, [isCurrent, timeMode, trackingMode, isHistoryActive, isLiveHistoryActive]);
};

export default useSyncLegacyTimeMode;
